import { makeAutoObservable, runInAction } from "mobx";
import { WorkflowEngine } from "../workflows/WorkflowEngine";
import novaApiClient from "../api/novaApiClient";
import {
  ChatMessage,
  WorkflowState,
  WorkflowType,
} from "../types/workflow.types";

export class NovaStore {
  currentWorkflow: WorkflowState = WorkflowState.IDLE;
  isTyping: boolean = false;
  currentMessage: string = "";
  workflowContext: any = {};
  messages: ChatMessage[] = [];
  loading: boolean = false;
  isGenerating: boolean = false;
  uploadedFile: File | null = null;
  encryptedFile: File | null = null;

  private workflowEngine: WorkflowEngine;

  constructor() {
    makeAutoObservable(this);
    this.workflowEngine = new WorkflowEngine();

    // Initialize with welcome message
    this.addMessage(
      "assistant",
      "Hello Kard! I'm Nova, your intelligent SynkOS assistant. How can I help you today?"
    );

    // Reset workflow context
    this.workflowContext = { step: 0 };
  }

  initializeWorkflow() {
    // Reset everything to initial state
    this.currentWorkflow = WorkflowState.IDLE;
    this.workflowContext = { step: 0 };
    this.uploadedFile = null;
    this.encryptedFile = null;
    this.isTyping = false;
    this.isGenerating = false;

    // Clear messages and add welcome message
    this.messages = [];
    this.addMessage(
      "assistant",
      "Hello Kard! I'm Nova, your intelligent SynkOS assistant. How can I help you today?"
    );
  }

  async streamMessage(message: string, speed: number = 50) {
    this.isTyping = true;
    this.currentMessage = "";

    const words = message.split(" ");

    for (const word of words) {
      await new Promise((resolve) => setTimeout(resolve, speed));
      runInAction(() => {
        this.currentMessage += word + " ";
      });
    }

    runInAction(() => {
      this.isTyping = false;
      this.isGenerating = false;
      this.addMessage("assistant", message);
    });
  }

  addMessage(
    role: "user" | "assistant",
    content: string,
    attachedFile?: File | null
  ) {
    this.messages.push({
      id: crypto.randomUUID(),
      role,
      content,
      timestamp: Date.now(),
      attachedFile, // Include the attached file in the message
    });
  }

  setGenerating(generating: boolean) {
    this.isGenerating = generating;
  }

  async processUserInput(message: string, attachedFile?: File | null) {
    console.log(
      "🔵 Nova - Processing user input:",
      message,
      "Step:",
      this.workflowContext.step,
      "File:",
      attachedFile?.name
    );

    const step = this.workflowContext.step || 0;

    if (step === 0) {
      // First interaction - User sends anything, we respond with plan
      await this.handleFirstInteraction();
    } else if (step === 1) {
      // Second interaction - User sends anything, we respond and open SynkPass
      await this.handleSecondInteraction();
    } else if (step === 2) {
      // User sent a message at step 2, but we're waiting for file upload
      // Just acknowledge and wait for file (no specific message as per scenario)
      console.log("🔵 Nova - Step 2: Waiting for file upload");
    } else if (step === 3) {
      // User sent a message at step 3, but we're waiting for encrypted file upload
      // Just acknowledge and wait for file (no specific message as per scenario)
      console.log("🔵 Nova - Step 3: Waiting for encrypted file upload");
    } else if (step === 4) {
      // Fifth interaction - User sends anything, we respond and open SynkSwap
      await this.handleFifthInteraction();
    } else {
      // Workflow completed
      await this.streamMessage(
        "Glad I could help! If you need anything else, don't hesitate to ask."
      );
    }
  }

  private async handleFirstInteraction() {
    console.log("🔵 Nova - Step 0: First interaction");

    const response = `Hello Kard, mission clear.

Here's the plan:

1. Generate a strong password with **SynkPass**
2. Encrypt your file using **SynkCrypt**
3. Send it securely via P2P with **SynkSmash**

I suggest using **james.files** as the identifier.

Do you also want me to pre-fill your username?`;

    await this.streamMessage(response);

    // Move to next step
    this.workflowContext.step = 1;
    this.workflowContext.targetId = "james.files";
    this.workflowContext.username = "kardon";
  }

  private async handleSecondInteraction() {
    console.log("🔵 Nova - Step 1: Second interaction");

    const response = `Great, **Kardon**, I'll get **SynkPass** ready for you.

Once your password is generated, keep it safe.

In the meantime, you can already send me the file you want to encrypt to save time.`;

    await this.streamMessage(response);

    // Wait a bit then open SynkPass
    setTimeout(async () => {
      await this.openSynkPass();
    }, 1000);

    // Move to next step
    this.workflowContext.step = 2;
  }

  private async handleThirdInteraction() {
    console.log("🔵 Nova - Step 2: Third interaction");
    console.log("🔵 Nova - About to send response and open SynkCrypt");

    const response = `File received. Thanks, **Kardon**.

I'll now open **SynkCrypt** for you.

All you need to do is:

• Insert the generated password
• Click on **"ENCRYPT FILES"**

Once the encryption is done, send me the encrypted file so I can prepare it for James.`;

    await this.streamMessage(response);

    // Wait a bit then open SynkCrypt
    setTimeout(async () => {
      console.log("🔵 Nova - Opening SynkCrypt after delay...");
      await this.openSynkCrypt();
    }, 1000);

    // Move to next step
    this.workflowContext.step = 3;
    console.log("🔵 Nova - Moved to step 3, waiting for encrypted file upload");
  }

  private async handleFourthInteraction() {
    console.log("🔵 Nova - Step 3: Fourth interaction");

    const response = `Encrypted file received. Moving forward. 🔐

I'll open **SynkSmash** to finalize the transfer.

You can:

• Add a password to secure the transfer (optional)
• Get a secure sharing link for James

And there you go. Mission almost accomplished.`;

    await this.streamMessage(response);

    // Wait a bit then open SynkSmash
    setTimeout(async () => {
      await this.openSynkSmash();
    }, 1000);

    // Move to next step
    this.workflowContext.step = 4;
  }

  private async handleFifthInteraction() {
    console.log("🔵 Nova - Step 4: Fifth interaction");

    const response = `Of course, **Kardon**. With **SynkSwap**, it's instant.

I'll open the app for you with:

• **0.05 ETH** as input
• **SYNK** as output

All that's left is for you to confirm the transaction.

You handle security, I'll handle simplicity. 🚀`;

    await this.streamMessage(response);

    // Wait a bit then open SynkSwap
    setTimeout(async () => {
      await this.openSynkSwap();
    }, 1000);

    // Move to next step
    this.workflowContext.step = 5;
  }

  private async openSynkPass() {
    try {
      console.log("🔑 Nova - Opening SynkPass");
      await this.workflowEngine.executeStep(WorkflowState.SYNKPASS, {
        type: "file_encryption" as WorkflowType,
        userMessage: "Opening SynkPass for password generation",
        targetId: this.workflowContext.targetId,
        username: this.workflowContext.username,
        step: 1,
      });
    } catch (error) {
      console.error("Error opening SynkPass:", error);
    }
  }

  private async openSynkCrypt() {
    try {
      console.log("🔐 Nova - Opening SynkCrypt");
      await this.workflowEngine.executeStep(WorkflowState.SYNKCRYPT, {
        type: "file_encryption" as WorkflowType,
        userMessage: "Opening SynkCrypt for file encryption",
        step: 2,
        integrationUrl: this.workflowContext.integrationUrl,
      });
    } catch (error) {
      console.error("Error opening SynkCrypt:", error);
    }
  }

  private async openSynkSmash() {
    try {
      console.log("📤 Nova - Opening SynkSmash");
      console.log(
        "📤 Nova - Using integrationUrl:",
        this.workflowContext.shareUrl
      );

      await this.workflowEngine.executeStep(WorkflowState.SYNKSMAH, {
        type: "file_encryption" as WorkflowType,
        userMessage: "Opening SynkSmash for P2P sending",
        step: 3,
        integrationUrl: this.workflowContext.shareUrl, // This should now be the integrationUrl
      });
    } catch (error) {
      console.error("Error opening SynkSmash:", error);
    }
  }

  private async openSynkSwap() {
    try {
      console.log("💱 Nova - Opening SynkSwap");
      await this.workflowEngine.executeStep(WorkflowState.SYNKSWAP, {
        type: "file_encryption" as WorkflowType,
        userMessage: "Opening SynkSwap for crypto swap",
        step: 5,
      });
    } catch (error) {
      console.error("Error opening SynkSwap:", error);
    }
  }

  async handleFileUpload(file: File) {
    console.log("📁 Nova - File uploaded:", file.name, file.size);
    console.log("📁 Nova - Current step:", this.workflowContext.step);
    console.log("📁 Nova - Workflow context:", this.workflowContext);

    this.uploadedFile = file;
    this.workflowContext.file = file;

    // Add the file to the last user message if it exists and has no file
    const lastUserMessage = this.messages
      .filter((m) => m.role === "user")
      .pop();

    if (lastUserMessage && !lastUserMessage.attachedFile) {
      lastUserMessage.attachedFile = file;
    }

    // Set generating state
    this.setGenerating(true);

    if (this.workflowContext.step === 2) {
      console.log("📁 Nova - Step 2: Uploading to SynkCrypt...");
      const result = await this.uploadFileToSynkCrypt(file);

      if (result.success) {
        console.log("✅ Nova - SynkCrypt upload successful");
        this.workflowContext.integrationUrl = result.integrationUrl;

        // Now trigger the third interaction to open SynkCrypt
        console.log("📁 Nova - Triggering handleThirdInteraction...");
        await this.handleThirdInteraction();
      } else {
        console.error("❌ Nova - SynkCrypt upload failed:", result.error);
        await this.streamMessage(
          "❌ Sorry, I couldn't upload your file. Let me try a different approach."
        );
      }
    } else if (this.workflowContext.step === 3) {
      console.log(
        "📁 Nova - Step 3: File received but should be encrypted file"
      );
      console.log("📁 Nova - Redirecting to encrypted file handler...");
      // If we're at step 3 and receive a file, treat it as an encrypted file
      await this.handleEncryptedFileUpload(file);
    } else {
      console.log(
        "📁 Nova - Not at step 2 or 3, current step:",
        this.workflowContext.step
      );
      console.log("📁 Nova - This might be why the workflow is not continuing");
    }
  }

  async handleEncryptedFileUpload(file: File) {
    console.log("🔐 Nova - Encrypted file uploaded:", file.name, file.size);
    console.log("🔐 Nova - Current step:", this.workflowContext.step);
    console.log("🔐 Nova - Workflow context:", this.workflowContext);

    this.encryptedFile = file;
    this.workflowContext.encryptedFile = file;

    // Add the encrypted file to the last user message if it exists and has no file
    const lastUserMessage = this.messages
      .filter((m) => m.role === "user")
      .pop();

    if (lastUserMessage && !lastUserMessage.attachedFile) {
      lastUserMessage.attachedFile = file;
    }

    // Set generating state
    this.setGenerating(true);

    // Check if we're at the right step for encrypted file upload
    if (this.workflowContext.step === 3) {
      console.log("🔐 Nova - Step 3: Uploading to SynkSmash...");
      const result = await this.uploadFileToSynkSmash(file);

      if (result.success) {
        console.log("✅ Nova - SynkSmash upload successful");
        console.log("🔐 Nova - API response:", result);
        console.log("🔐 Nova - Using integrationUrl:", result.integrationUrl);

        this.workflowContext.shareUrl = result.integrationUrl; // Store in shareUrl for backward compatibility
        this.workflowContext.fileId = result.fileId;

        // Now trigger the fourth interaction to open SynkSmash
        console.log("🔐 Nova - Triggering handleFourthInteraction...");
        await this.handleFourthInteraction();
      } else {
        console.error("❌ Nova - SynkSmash upload failed:", result.error);
        await this.streamMessage(
          "❌ Sorry, I couldn't upload your encrypted file. Let me try a different approach."
        );
      }
    } else {
      console.log(
        "🔐 Nova - Not at step 3, current step:",
        this.workflowContext.step
      );
      console.log("🔐 Nova - This might be why the workflow is not continuing");

      // If we're not at step 3, try to continue anyway
      if (this.workflowContext.step === 2) {
        console.log("🔐 Nova - At step 2, this might be the first file upload");
        // This might be the first file upload, not the encrypted one
        await this.handleFileUpload(file);
      } else {
        console.log("🔐 Nova - Unknown step, cannot process encrypted file");
        await this.streamMessage(
          "❌ I'm not sure what to do with this file at step " +
            this.workflowContext.step
        );
      }
    }
  }

  private async uploadFileToSynkCrypt(file: File) {
    try {
      console.log("🔐 Nova - Starting SynkCrypt upload...");

      // Try real API first, fallback to mock if it fails
      let result;
      try {
        result = await novaApiClient.uploadToSynkCrypt(file);
      } catch (error) {
        console.log(
          "🔄 Nova - CORS/API error, falling back to mock API for SynkCrypt"
        );
        result = await novaApiClient.mockApiCall("synkcrypt", "upload", {
          file,
        });
      }

      if (result.success) {
        this.workflowContext.integrationUrl = result.integrationUrl;
        console.log(
          "✅ Nova - File uploaded to SynkCrypt successfully:",
          result.integrationUrl
        );
        return { success: true, integrationUrl: result.integrationUrl };
      } else {
        console.error(
          "❌ Nova - Failed to upload file to SynkCrypt:",
          result.error
        );
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error("❌ Nova - Error uploading file to SynkCrypt:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  private async uploadFileToSynkSmash(file: File) {
    try {
      console.log("📤 Nova - Starting SynkSmash upload...");

      // Try real API first, fallback to mock if it fails
      let result;
      try {
        result = await novaApiClient.uploadToSynkSmash(file);
      } catch (error) {
        console.log(
          "🔄 Nova - CORS/API error, falling back to mock API for SynkSmash"
        );
        result = await novaApiClient.mockApiCall("synksmash", "upload", {
          file,
        });
      }

      if (result.success) {
        // Use integrationUrl from API response (like SynkCrypt)
        this.workflowContext.shareUrl = result.integrationUrl;
        this.workflowContext.fileId = result.fileId;
        console.log(
          "✅ Nova - File uploaded to SynkSmash successfully:",
          result.integrationUrl
        );
        return {
          success: true,
          integrationUrl: result.integrationUrl, // Return integrationUrl directly
          fileId: result.fileId,
        };
      } else {
        console.error(
          "❌ Nova - Failed to upload file to SynkSmash:",
          result.error
        );
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error("❌ Nova - Error uploading file to SynkSmash:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  continueWorkflow() {
    this.workflowEngine.continueWorkflow(this.workflowContext);
  }

  private analyzeMessage(message: string): WorkflowType | null {
    const lowerMessage = message.toLowerCase();

    if (
      lowerMessage.includes("file") ||
      lowerMessage.includes("send") ||
      lowerMessage.includes("encrypt")
    ) {
      return "file_encryption";
    }

    if (lowerMessage.includes("password")) {
      return "password_generation";
    }

    return null;
  }
}
